import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useShippingSenders } from './useShippingSenders';
import { OnboardingInstructions, ShippingSenderDialog } from './components';
import { shippingSenderService } from '../../../services/shippingSenderService';
import { aiBalanceService } from '../../../services/aiBalanceService';
import type { ShippingSender } from '../../../types/shippingSender';

export default function SettingsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const companyId = id ? parseInt(id) : null;

  const { data, save, deactivate } = useShippingSenders({ companyId });
  const { senders, loading, error } = data;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSender, setEditingSender] = useState<ShippingSender | null>(null);
  const [aiBalanceUsd, setAiBalanceUsd] = useState<number | null>(null);
  const [deactivatingSender, setDeactivatingSender] = useState<ShippingSender | null>(null);

  const fetchApiKeyForSearch = useCallback(
    async (senderId: number) => {
      if (!companyId) {
        throw new Error('Ідентифікатор компанії відсутній');
      }
      return shippingSenderService.getApiKeyForFrontend(companyId, senderId);
    },
    [companyId]
  );

  useEffect(() => {
    const fetchAiBalance = async () => {
      if (!companyId) {
        return;
      }

      try {
        const balance = await aiBalanceService.getBalance(companyId);
        setAiBalanceUsd(balance.balanceUsd);
      } catch (err) {
        console.error('Error fetching AI balance:', err);
      }
    };

    fetchAiBalance();
  }, [companyId]);

  const openCreateDialog = () => {
    setEditingSender(null);
    setDialogOpen(true);
  };

  const openEditDialog = (sender: ShippingSender) => {
    setEditingSender(sender);
    setDialogOpen(true);
  };

  const handleConfirmDeactivate = async () => {
    if (!deactivatingSender) return;
    await deactivate.run(deactivatingSender.id);
    setDeactivatingSender(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      <Box
        sx={{
          position: 'sticky',
          top: 64,
          zIndex: 10,
          backgroundColor: 'background.default',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          py: 1,
          mb: 2,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/companies/${id}/home`)}
          size="small"
        >
          На головну
        </Button>
        <Typography
          variant="button"
          component="h1"
          noWrap
          sx={{
            minWidth: 0,
            pr: { xs: '9px', sm: '10px' },
            color: 'primary.main',
          }}
        >
          Налаштування
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {aiBalanceUsd !== null && (
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>AI-баланс</Typography>
          <Typography variant="body2" color="text.secondary">
            Поточна сума: ${aiBalanceUsd.toFixed(2)}
          </Typography>
        </Paper>
      )}

      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6">Доставка — Нова пошта</Typography>
          <Button size="small" startIcon={<AddIcon />} onClick={openCreateDialog}>
            Додати відправника
          </Button>
        </Box>

        <OnboardingInstructions />

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Кожен профіль — окремий акаунт Нової пошти (напр. окремий ФОП). Користувач обирає
          потрібний при створенні кожної ТТН.
        </Typography>

        {senders.length === 0 && (
          <Alert severity="info">Ще немає жодного профілю відправника — додайте перший.</Alert>
        )}

        {senders.map((sender) => (
          <Box
            key={sender.id}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              py: 1.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
              '&:last-of-type': { borderBottom: 'none' },
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" noWrap>{sender.label}</Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {sender.apiKeyConfigured ? 'Ключ налаштовано' : 'Ключ не налаштовано'}
                {sender.defaultSenderCityRef && sender.defaultSenderWarehouseRef
                  ? ' · Адреса відправлення вказана'
                  : ' · Адресу відправлення не вказано'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
              <IconButton size="small" onClick={() => openEditDialog(sender)}>
                <EditIcon fontSize="small" />
              </IconButton>
              <Tooltip title="Прибрати профіль">
                <IconButton
                  size="small"
                  color="error"
                  disabled={deactivate.submittingSenderId === sender.id}
                  onClick={() => setDeactivatingSender(sender)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        ))}

        {deactivate.error && <Alert severity="error" sx={{ mt: 2 }}>{deactivate.error}</Alert>}
      </Paper>

      <Dialog open={Boolean(deactivatingSender)} onClose={() => setDeactivatingSender(null)}>
        <DialogTitle>Прибрати профіль відправника?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Профіль «{deactivatingSender?.label}» більше не можна буде обрати для нових ТТН.
            Цю дію не можна скасувати.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivatingSender(null)} disabled={deactivate.submittingSenderId !== null}>
            Не прибирати
          </Button>
          <Button
            onClick={handleConfirmDeactivate}
            color="error"
            disabled={deactivate.submittingSenderId !== null}
          >
            {deactivate.submittingSenderId !== null ? 'Прибираємо...' : 'Прибрати профіль'}
          </Button>
        </DialogActions>
      </Dialog>

      {companyId && (
        <ShippingSenderDialog
          open={dialogOpen}
          sender={editingSender}
          onClose={() => setDialogOpen(false)}
          onCreate={save.create}
          onUpdate={save.update}
          submitting={save.saving}
          error={save.error}
          fetchApiKeyForSearch={fetchApiKeyForSearch}
        />
      )}
    </Box>
  );
}
